import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/i18n';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Icons } from '@/utils/Icons';
import { useLazyGetMatchesGroupedByCompetitionQuery } from '@/store/api/sportsApi';
import { useLazyGetCasinoGamesQuery } from '@/store/api/casinoApi';
import { useCasinoGameLauncher } from '@/hooks/useCasinoGameLauncher';
import SportIcon from '@/components/common/SportIcon';
import { getSportLabel } from '@/config/sports';

const MAX_MATCHES = 8;
const MAX_GAMES = 6;

const formatStart = (startTime: string) => {
  const start = dayjs(startTime);
  if (!start.isValid()) return '';
  if (start.isSame(dayjs(), 'day')) return `Today, ${start.format('h:mm A')}`;
  if (start.isSame(dayjs().add(1, 'day'), 'day')) return `Tomorrow, ${start.format('h:mm A')}`;
  return start.format('D MMM, h:mm A');
};

/** Highlights the part of `text` that matches the search query. */
const Highlight = ({ text, query }: { text: string; query: string }) => {
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (!query || index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <span className="text-gold">{text.slice(index, index + query.length)}</span>
      {text.slice(index + query.length)}
    </>
  );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="sticky top-0 z-10 bg-shell-panel px-4 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wider text-shell-muted">
    {children}
  </div>
);

/** Header search: matches (sports fixtures) and casino games in one dark dropdown. */
type SearchDropdownProps = {
  /** Short "Search" placeholder, for narrow boxes (the phone header). */
  compact?: boolean;
};

const SearchDropdown: React.FC<SearchDropdownProps> = ({ compact = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { launchCasinoGame } = useCasinoGameLauncher();

  const [searchMatches, { data: matchData, isFetching: fetchingMatches }] =
    useLazyGetMatchesGroupedByCompetitionQuery();
  const [searchGames, { data: gameData, isFetching: fetchingGames }] = useLazyGetCasinoGamesQuery();

  const term = query.trim();

  // Debounced search across both sources
  useEffect(() => {
    if (!term) return;
    const delay = setTimeout(() => {
      searchMatches({ search: term });
      searchGames({ search: term, status: 'ACTIVE', limit: 20, page: 1 });
    }, 400);
    return () => clearTimeout(delay);
  }, [term]);

  // Close on outside click / Escape
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const reset = () => {
    setQuery('');
    setOpen(false);
  };

  const matches = Object.values(matchData?.fixture ?? {})
    .flatMap((competitions) => Object.values(competitions).flat())
    .slice(0, MAX_MATCHES);
  // The casino search is fuzzy; keep games whose name actually contains the term.
  const games = (gameData?.games ?? [])
    .filter((game) => game.name.toLowerCase().includes(term.toLowerCase()))
    .slice(0, MAX_GAMES);

  const isSearching = fetchingMatches || fetchingGames;
  const showDropdown = open && term.length > 0;
  const noResults = !isSearching && matches.length === 0 && games.length === 0;

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="flex h-10 w-full items-center gap-2 rounded-lg border border-shell-border bg-shell-raised px-3 focus-within:border-gold">
        <Icons.Search className="shrink-0 text-shell-muted" />
        <input
          type="text"
          placeholder={t(compact ? 'search.short' : 'search.placeholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full bg-transparent text-sm text-shell-fg outline-none placeholder:text-shell-muted"
        />
        {isSearching && term ? (
          <span className="size-4 shrink-0 animate-spin rounded-full border-2 border-shell-border border-t-gold" />
        ) : (
          query && (
            <button
              type="button"
              onClick={reset}
              aria-label="Clear search"
              className="shrink-0 text-shell-muted hover:text-shell-fg"
            >
              <Icons.Cross size={16} />
            </button>
          )
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 top-12 z-50 max-h-[70vh] w-full min-w-[320px] overflow-y-auto rounded-xl border border-shell-border bg-shell-panel pb-2 shadow-2xl">
          {matches.length > 0 && (
            <>
              <SectionLabel>{t('search.matches')}</SectionLabel>
              {matches.map((match) => (
                <button
                  key={match.id}
                  type="button"
                  onClick={() => {
                    navigate(`/game-details/${match.sport}/${match.id}`);
                    reset();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-shell-raised"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-shell">
                    <SportIcon
                      name={getSportLabel(match.sport) ?? match.sport}
                      className="size-4"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-shell-fg">
                      <Highlight text={match.name} query={term} />
                    </span>
                    <span className="block truncate text-xs text-shell-muted">
                      {match.competition?.name}
                    </span>
                  </span>
                  {match.inplay ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-success-emerald/15 px-2 py-0.5 text-[11px] font-semibold text-success-emerald">
                      <span className="size-1.5 animate-pulse rounded-full bg-success-emerald" />
                      Live
                    </span>
                  ) : (
                    <span className="shrink-0 text-[11px] text-shell-muted">
                      {formatStart(match.startTime)}
                    </span>
                  )}
                </button>
              ))}
            </>
          )}

          {games.length > 0 && (
            <>
              <SectionLabel>{t('search.casinoGames')}</SectionLabel>
              {games.map((game) => (
                <button
                  key={game.id}
                  type="button"
                  onClick={() => {
                    reset();
                    launchCasinoGame(game.id, 'mac88');
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left transition hover:bg-shell-raised"
                >
                  <img
                    src={game.gameImage}
                    alt=""
                    loading="lazy"
                    onError={(e) => (e.currentTarget.src = '/images/casino/casino-fallback.webp')}
                    className="aspect-[5/3] w-14 shrink-0 rounded-md object-cover"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-shell-fg">
                      <Highlight text={game.name} query={term} />
                    </span>
                    <span className="block truncate text-xs text-shell-muted">
                      {game.gameProviderName}
                    </span>
                  </span>
                  <Icons.Forward size={10} className="shrink-0 text-shell-muted" />
                </button>
              ))}
            </>
          )}

          {isSearching && matches.length === 0 && games.length === 0 && (
            <p className="px-4 pt-4 text-center text-sm text-shell-muted">
              {t('search.searching')}
            </p>
          )}

          {noResults && (
            <div className="px-4 pt-6 text-center">
              <p className="text-sm text-shell-fg">{t('search.noResults', { term })}</p>
              <p className="mt-1 pb-2 text-xs text-shell-muted">{t('search.hint')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
